import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '@shared/infrastructure/ui/layout/dashboardLayout';
import {
  TablePlain,
  TableCell,
  TableRow
} from '@shared/infrastructure/ui/components/table/tablePlain/tablePlain';
import { subtopicRepository } from '@shared/domain/subtopic/subtopic.repository';
import { useAppSelector } from '@shared/infrastructure/state/store';
import {
  countryCodeSelector,
  languageCodeSelector
} from '@shared/infrastructure/state/global.slice';
import { Subtopic } from '@shared/domain/subtopic/subtopic.aggregate';
import { PageHeaderWithFilter } from '@shared/infrastructure/ui/components/header/pageHeader';
import { topicRepository } from '@shared/domain/topic/topic.repository';
import { Topic } from '@shared/domain/topic/topic.aggregate';
import EditIcon from '@mui/icons-material/CreateOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fab,
  IconButton,
  TextField,
  Tooltip
} from '@mui/material';
import { historyRepository } from '@shared/domain/router/router.repository';
import { ROUTES } from '@shared/infrastructure/router/paths';
import { routeRepository } from '@shared/domain/router/route/route.repository';
import { Therapy } from '@shared/domain/therapy/therapy.aggregate';
import { therapyRepository } from '@shared/domain/therapy/therapy.repository';

const Row = ({
  therapyId,
  topicId,
  subtopic,
  onDelete
}: {
  therapyId: number;
  topicId: number;
  subtopic: Subtopic;
  onDelete: () => void;
}) => {
  return (
    <TableRow
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
      aria-label='subtopics table row'
    >
      <TableCell component='th' scope='row'>
        {subtopic.nameLanguage}
      </TableCell>
      <TableCell>{subtopic.topicNameLanguage}</TableCell>
      <TableCell>{subtopic.descriptionLanguage}</TableCell>
      <TableCell>
        <Tooltip title='Edit subtopic' placement='left-start'>
          <IconButton
            size='large'
            aria-label='Edit subtopic'
            onClick={() =>
              historyRepository.push(
                routeRepository.buildRoute(ROUTES.SUBTOPICS_EDIT.path, {
                  therapyId: String(therapyId),
                  topicId: String(topicId),
                  subtopicId: String(subtopic.id)
                })
              )
            }
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title='Delete subtopic' placement='left-start'>
          <IconButton
            size='large'
            aria-label='Delete subtopic'
            onClick={onDelete}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

const DeleteDialog = ({
  subtopic,
  onCancel,
  onAccept
}: {
  subtopic?: Subtopic;
  onCancel?: () => void;
  onAccept?: (subtopic: Subtopic) => void;
}) => {
  return (
    <Dialog
      open={!!subtopic}
      onClose={onCancel}
      aria-labelledby='delete-dialog-title'
      aria-describedby='delete-dialog-description'
    >
      <DialogTitle id='delete-dialog-title'>Are you sure?</DialogTitle>
      <DialogContent>
        <DialogContentText id='delete-dialog-description'>
          The subtopic will be permanently lost.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onCancel} autoFocus>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='error'
          onClick={() => onAccept?.(subtopic!)}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const SubtopicView = () => {
  const countryCode = useAppSelector(countryCodeSelector);
  const languageCode = useAppSelector(languageCodeSelector);
  const params =
    routeRepository.getParams(
      ROUTES.SUBTOPICS_FILTERED.path,
      window.location.pathname
    ) || {};

  const [therapies, setTherapies] = useState<Therapy[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [subtopics, setSubtopics] = useState<Subtopic[]>([]);

  const [therapyId, setTherapyId] = useState<number>(Number(params.therapyId));
  const [topicId, setTopicId] = useState<number>(Number(params.topicId));
  const [deleteSubtopic, setDeleteSubtopic] = useState<Subtopic>();

  useEffect(() => {
    therapyRepository
      .getTherapies(countryCode, languageCode)
      .then((therapies) => {
        setTherapies(therapies);
        setTherapyId(
          (id) => (therapies.find((th) => th.id === id) ?? therapies[0])?.id
        );
      });
  }, [countryCode, languageCode]);
  useEffect(() => {
    if (!Number.isNaN(therapyId)) {
      topicRepository.getTopics(countryCode, languageCode).then((topics) => {
        topics = topics.filter((t) => t.therapyId === therapyId);
        setTopics(topics);
        setTopicId((id) => (topics.find((t) => t.id === id) ?? topics[0])?.id);
      });
    }
  }, [countryCode, languageCode, therapyId]);
  useEffect(() => {
    if (!Number.isNaN(topicId)) {
      subtopicRepository
        .getSubtopics(countryCode, languageCode, topicId)
        .then((subtopics) => setSubtopics(subtopics));
    }
  }, [countryCode, languageCode, topicId]);
  useEffect(() => {
    if (!Number.isNaN(therapyId) && !Number.isNaN(topicId)) {
      historyRepository.push(
        routeRepository.buildRoute(ROUTES.SUBTOPICS_FILTERED.path, {
          therapyId: String(therapyId),
          topicId: String(topicId)
        })
      );
    }
  }, [therapyId, topicId]);

  return (
    <DashboardLayout>
      <PageHeaderWithFilter title='Subtopics View'>
        <Box sx={{ display: 'flex' }}>
          <Autocomplete
            options={therapies}
            getOptionLabel={(therapy) => therapy.nameLanguage}
            renderInput={(params) => <TextField {...params} label='Therapy' />}
            value={therapies.find((t) => t.id === therapyId) ?? null}
            onChange={(_, therapy) => setTherapyId((id) => therapy?.id ?? id)}
            size='small'
            sx={{ minWidth: 300 }}
          />
          <Autocomplete
            options={topics}
            getOptionLabel={(topic) => topic.nameLanguage}
            renderInput={(params) => <TextField {...params} label='Topic' />}
            value={topics.find((t) => t.id === topicId) ?? null}
            onChange={(_, topic) => setTopicId((id) => topic?.id ?? id)}
            size='small'
            sx={{ minWidth: 300 }}
          />
        </Box>
      </PageHeaderWithFilter>
      <TablePlain<Subtopic[]>
        data={subtopics}
        headers={['Name', 'Topic', 'Description', '']}
      >
        {(data: Subtopic[]) =>
          therapyId &&
          topicId &&
          data.map((subtopic: Subtopic) => (
            <Row
              key={subtopic.id}
              therapyId={therapyId}
              topicId={topicId}
              subtopic={subtopic}
              onDelete={() => setDeleteSubtopic(subtopic)}
            />
          ))
        }
      </TablePlain>
      <Fab
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        color='primary'
        aria-label='Create'
        onClick={() =>
          historyRepository.push(
            routeRepository.buildRoute(ROUTES.SUBTOPICS_CREATE.path, {
              therapyId: String(therapyId),
              topicId: String(topicId)
            })
          )
        }
      >
        <AddIcon />
      </Fab>
      <DeleteDialog
        subtopic={deleteSubtopic}
        onCancel={() => setDeleteSubtopic(undefined)}
        onAccept={(subtopic) => {
          subtopicRepository
            .deleteSubtopic(countryCode, subtopic.id)
            .then(() => {
              setSubtopics((subtopics) =>
                subtopics.filter((s) => s.id !== subtopic.id)
              );
              setDeleteSubtopic(undefined);
            });
        }}
      />
    </DashboardLayout>
  );
};
